import { ReactNode, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { ArrowsRightLeftIcon, CheckIcon, TruckIcon } from "@heroicons/react/24/outline";

import { IPart } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import Tooltip from "../shared/Tooltip";
import VehicleCompareModal from "../shared/VehicleCompareModal";

export type VehicleSearchHighlights = Partial<{
  vrm: string;
  make: string;
  model: string;
  trim: string;
  year: string | number;
  yearFrom: string | number;
  yearTo: string | number;
  bodyStyle: string;
  numberOfDoors: string | number;
  fuelType: string;
  transmissionType: string;
  engineCode: string;
  engineSize: string | number;
}>;

const norm = (v: unknown) => v === null || v === undefined ? "" : String(v).trim().toLowerCase();

const includesMatch = (haystack: unknown, needle: unknown) => {
  const h = norm(haystack);
  const n = norm(needle);
  return n.length > 0 && h.length > 0 && h.includes(n);
};

function CopyText({ text, className = "", title }: { text: string | number | null | undefined; className?: string; title?: string }) {
  const { t } = useTranslation("stock");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1200);
    return () => clearTimeout(timer);
  }, [copied]);

  if (text === null || text === undefined || text === "") return null;
  const value = String(text);

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value).then(() => setCopied(true));
      }}
      title={title ?? (copied ? t("Copied!") : t("Click to copy"))}
      className={classNames(
        "inline-flex max-w-full items-start gap-1 rounded px-1 -mx-1 text-left transition-all duration-150 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-400",
        copied ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200" : "hover:bg-slate-100 hover:text-primary-600",
        className
      )}
    >
      {copied ? (
        <>
          <CheckIcon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
          <span className="font-medium">{t("Copied!")}</span>
        </>
      ) : (
        <span className="min-w-0 break-words 2xl:truncate">{value}</span>
      )}
    </button>
  );
}

export default function DonorVehicleCell({
  part,
  twoColumn = false,
  highlights = {},
  searchVrm = "",
}: {
  part: IPart;
  twoColumn?: boolean;
  highlights?: VehicleSearchHighlights;
  searchVrm?: string;
}) {
  const { t } = useTranslation("stock");
  const [compareOpen, setCompareOpen] = useState(false);
  const vehicle = part.vehicle as IVehicle | null;

  if (!vehicle) return <span className="text-xs text-slate-400">-</span>;

  const mileage = typeof vehicle.odometerInMiles === "number" ? `${vehicle.odometerInMiles.toLocaleString("en-GB")} mi` : null;
  const body = [vehicle.numberOfDoors ? `${vehicle.numberOfDoors}dr` : null, vehicle.bodyStyle || vehicle.bodyStyleDescription || null].filter(Boolean).join(" ");
  const fuel = vehicle.combinedFuelType || vehicle.fuelType || vehicle.fuelDescription || null;
  const transmission = vehicle.combinedTransmission || vehicle.dvlaTransmission || vehicle.transmission || null;
  const yearNum = vehicle.dvlaYearOfManufacture ?? null;
  const yearMatched = (() => {
    if (yearNum === null) return false;
    if (highlights.year && includesMatch(yearNum, highlights.year)) return true;
    const from = highlights.yearFrom ? Number(highlights.yearFrom) : null;
    const to = highlights.yearTo ? Number(highlights.yearTo) : null;
    if (from !== null && !Number.isNaN(from) && yearNum < from) return false;
    if (to !== null && !Number.isNaN(to) && yearNum > to) return false;
    return (from !== null && !Number.isNaN(from)) || (to !== null && !Number.isNaN(to));
  })();

  const fields: { label: string; value: ReactNode; matched?: boolean }[] = [
    { label: t("Reg"), value: vehicle.vrm ? <CopyText text={vehicle.vrm} /> : null, matched: includesMatch(vehicle.vrm, highlights.vrm) },
    { label: t("VIN"), value: vehicle.combinedVIN || vehicle.vinOriginalDVLA ? <CopyText text={vehicle.combinedVIN || vehicle.vinOriginalDVLA} /> : null },
    { label: t("Year"), value: yearNum, matched: yearMatched },
    { label: t("Make"), value: vehicle.combinedMake || null, matched: includesMatch(vehicle.combinedMake, highlights.make) },
    { label: t("Model"), value: vehicle.combinedModel || null, matched: includesMatch(vehicle.combinedModel, highlights.model) },
    { label: t("Trim"), value: vehicle.modelVariantDescription || null, matched: includesMatch(vehicle.modelVariantDescription, highlights.trim) },
    { label: t("Mileage"), value: mileage },
    { label: t("Body"), value: body || null, matched: includesMatch(body, highlights.bodyStyle) || includesMatch(vehicle.numberOfDoors, highlights.numberOfDoors) },
    { label: t("Fuel"), value: fuel, matched: includesMatch(fuel, highlights.fuelType) },
    { label: t("Engine Code"), value: vehicle.engineModelCode ? <CopyText text={vehicle.engineModelCode} /> : null, matched: includesMatch(vehicle.engineModelCode, highlights.engineCode) },
    { label: t("Transmission"), value: transmission, matched: includesMatch(transmission, highlights.transmissionType) },
    { label: t("BHP"), value: typeof vehicle.maximumPowerBHP === "number" ? `${vehicle.maximumPowerBHP} bhp` : null },
  ].filter((f) => f.value !== null && f.value !== "");

  const renderFieldList = (list: typeof fields, useTwoColumns = false) => (
    <dl className={classNames("grid min-w-0 grid-cols-1 gap-x-3 gap-y-1 text-xs", useTwoColumns ? "2xl:grid-cols-2" : "")}>
      {list.map((field) => (
        <div key={field.label} className="min-w-0 2xl:grid 2xl:grid-cols-[auto,minmax(0,1fr)] 2xl:gap-x-2">
          <dt className={classNames("text-[11px] font-medium leading-tight 2xl:text-xs", field.matched ? "text-primary-600" : "text-slate-400")}>{field.label}</dt>
          <dd className={classNames("min-w-0 break-words leading-tight 2xl:truncate", field.matched ? "font-semibold text-primary-700" : "text-slate-700")}>{field.value}</dd>
        </div>
      ))}
    </dl>
  );

  return (
    <div className="flex min-w-[16rem] flex-col gap-2 2xl:min-w-[24rem] 2xl:flex-row 2xl:items-start">
      <div className="order-1 flex flex-none items-center gap-1 2xl:order-2 2xl:mt-0.5 2xl:flex-col">
        {vehicle.id && (
          <Tooltip content={t("View vehicle")}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab(`/vehicles/${vehicle.id}`);
              }}
              aria-label={t("View vehicle")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white shadow-sm ring-1 ring-primary-700/20 transition-all hover:bg-primary-700"
            >
              <TruckIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </Tooltip>
        )}
        <Tooltip content={t("Compare vehicle")}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCompareOpen(true);
            }}
            aria-label={t("Compare vehicle")}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-slate-600 shadow-sm ring-1 ring-slate-300 transition-all hover:bg-slate-50 hover:text-primary-600"
          >
            <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
      <div className="order-2 min-w-0 flex-1 2xl:order-1 @container">
        {renderFieldList(fields, twoColumn)}
      </div>
      <VehicleCompareModal open={compareOpen} setOpen={setCompareOpen} donor={vehicle} initialVrm={searchVrm} />
    </div>
  );
}
